import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart, ChevronDown, Globe } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { t } from "@/lib/translations";

const WA = "https://wa.me/243835666498?text=Bonjour%20FGNB%2C%20je%20souhaite%20vous%20rejoindre%20!";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang } = useLang();
  const tx = t[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false); setDropdown(null);
    if (href.startsWith("/#")) {
      const anchor = href.slice(1);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => { const el = document.querySelector(anchor); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 300); }
      else { const el = document.querySelector(anchor); if (el) el.scrollIntoView({ behavior: "smooth" }); }
    } else { navigate(href); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  const navLinks = [
    { label: tx.nav.home, href: "/" },
    { label: tx.nav.foundation, children: [{ label: tx.nav.about, href: "/#apropos" }, { label: tx.nav.mission, href: "/#mission" }, { label: tx.nav.values, href: "/#valeurs" }] },
    { label: tx.nav.foundress, href: "/fondatrice" },
    { label: tx.nav.actions, children: [{ label: tx.nav.humanitarian, href: "/actions" }, { label: tx.nav.ceremonies, href: "/ceremonies" }, { label: tx.nav.team, href: "/equipe" }] },
    { label: tx.nav.gallery, href: "/galerie" },
    { label: tx.nav.contact, href: "/#contact" },
  ];

  return (
    <motion.header initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[oklch(0.40_0.18_210/0.96)] shadow-[0_4px_32px_oklch(0.40_0.18_210/0.35)]" : "bg-transparent"}`}
      style={{ backdropFilter: scrolled ? "blur(20px)" : "none" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => handleNav("/")} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shadow-[0_2px_12px_oklch(0.72_0.11_45/0.4)]"><Heart className="w-5 h-5 text-primary fill-current" /></div>
            <div className="flex flex-col leading-none">
              <span className="text-lg font-bold text-white tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>FGNB</span>
              <span className="text-[10px] text-accent uppercase tracking-[0.15em] font-medium">Fondation Gath Naël Beko</span>
            </div>
          </button>
          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => "children" in link ? (
              <div key={link.label} className="relative" onMouseEnter={() => setDropdown(link.label)} onMouseLeave={() => setDropdown(null)}>
                <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white/85 hover:text-accent transition-colors rounded-lg hover:bg-white/8">{link.label} <ChevronDown className="w-3.5 h-3.5" /></button>
                <AnimatePresence>{dropdown === link.label && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-1 w-52 bg-[oklch(0.28_0.16_210/0.98)] border border-white/15 rounded-2xl py-2 shadow-2xl">
                    {link.children.map((child) => (<button key={child.href} onClick={() => handleNav(child.href)} className="w-full text-left px-4 py-2.5 text-sm text-white/75 hover:text-accent hover:bg-white/5 transition-colors">{child.label}</button>))}
                  </motion.div>)}</AnimatePresence>
              </div>
            ) : (<button key={link.href} onClick={() => handleNav(link.href!)} className={`px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-white/8 ${location.pathname === link.href ? "text-accent" : "text-white/85 hover:text-accent"}`}>{link.label}</button>))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <div className="flex items-center gap-0.5 bg-white/10 rounded-full p-1 border border-white/15">
              <Globe className="w-3.5 h-3.5 text-white/50 ml-1" />
              <button onClick={() => setLang("fr")} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-all ${lang === "fr" ? "bg-accent text-primary" : "text-white/60 hover:text-white"}`}>FR</button>
              <button onClick={() => setLang("en")} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-all ${lang === "en" ? "bg-accent text-primary" : "text-white/60 hover:text-white"}`}>EN</button>
            </div>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 text-sm font-semibold rounded-full text-primary bg-accent hover:bg-accent/90 transition-all shadow-[0_2px_12px_oklch(0.72_0.11_45/0.35)]">{tx.nav.support}</a>
          </div>
          <div className="lg:hidden flex items-center gap-2">
            <div className="flex items-center gap-0.5 bg-white/10 rounded-full p-0.5">
              <button onClick={() => setLang("fr")} className={`px-2 py-0.5 text-xs font-bold rounded-full transition-all ${lang === "fr" ? "bg-accent text-primary" : "text-white/60"}`}>FR</button>
              <button onClick={() => setLang("en")} className={`px-2 py-0.5 text-xs font-bold rounded-full transition-all ${lang === "en" ? "bg-accent text-primary" : "text-white/60"}`}>EN</button>
            </div>
            <button className="text-white p-2 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
      </div>
      <AnimatePresence>{mobileOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-[oklch(0.28_0.16_210/0.98)] border-t border-white/10">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => "children" in link ? (
              <div key={link.label}><p className="px-4 py-2 text-xs text-white/40 uppercase tracking-wider font-semibold">{link.label}</p>
                {link.children.map((child) => (<button key={child.href} onClick={() => handleNav(child.href)} className="w-full text-left px-6 py-2.5 text-white/70 hover:text-accent text-sm transition-colors">{child.label}</button>))}
              </div>
            ) : (<button key={link.href} onClick={() => handleNav(link.href!)} className="text-left px-4 py-3 text-white/80 hover:text-accent font-medium rounded-lg hover:bg-white/5 transition-colors">{link.label}</button>))}
            <a href={WA} target="_blank" rel="noopener noreferrer" className="mt-2 px-5 py-3 font-semibold rounded-full text-primary bg-accent text-center block">{tx.nav.support}</a>
          </div>
        </motion.div>)}</AnimatePresence>
    </motion.header>
  );
}
